import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { ArrowRight, CurrencyBtc, Package, Truck } from "@phosphor-icons/react";

const CATEGORY_LABELS = { digital: "Digital", handmade: "Handmade", general: "General" };

export default function Shop() {
  const [params] = useSearchParams();
  const category = params.get("category") || "all";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/products${category !== "all" ? `?category=${category}` : ""}`)
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <main className="min-h-screen" data-testid="shop-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 flex flex-col justify-end">
            <p className="overline mb-6 text-signal">// 2026 drop · open now</p>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-6">
              Independent<br/>goods, shipped<br/><span className="font-light italic neon-text">worldwide.</span>
            </h1>
            <p className="font-sans text-base md:text-lg text-neutral-400 max-w-xl leading-relaxed mb-8">
              A compact shop for handmade objects and digital releases. Pay by card or crypto, ship by mail, or request local delivery.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/shop" className="btn-signal" data-testid="hero-shop-cta">Browse items <ArrowRight size={16}/></Link>
              <Link to="/delivery" className="btn-outline-dark" data-testid="hero-delivery-cta">Request delivery</Link>
              <Link to="/become-vendor" className="btn-outline-dark" data-testid="hero-vendor-cta">Sell here</Link>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-12 max-w-lg">
              {[
                { icon: <Truck size={20} weight="duotone"/>, k: "SHIP", v: "Mail + Local" },
                { icon: <CurrencyBtc size={20} weight="duotone"/>, k: "PAY", v: "Card + Crypto" },
                { icon: <Package size={20} weight="duotone"/>, k: "MAKE", v: "Small-batch" },
              ].map((f) => (
                <div key={f.k} className="border border-white/10 p-4 bg-[#111111]">
                  {f.icon}
                  <p className="overline mt-3 opacity-60">{f.k}</p>
                  <p className="font-mono text-sm mt-1">{f.v}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <img src="https://images.pexels.com/photos/37613537/pexels-photo-37613537.jpeg?auto=compress&cs=tinysrgb&w=940"
                 alt="Maker in studio"
                 className="w-full h-[520px] object-cover border border-white/10" />
            <div className="absolute -bottom-6 -left-6 bg-signal text-white px-6 py-4 font-mono text-xs tracking-widest uppercase shadow-[0_0_24px_rgba(255,7,58,0.6)]">
              Free shipping over $150
            </div>
          </div>
        </div>
      </section>

      {/* Category tabs */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12" id="grid">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="overline opacity-60 mb-2">// Catalog</p>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter">
              {category === "all" ? "All items" : CATEGORY_LABELS[category] || category}
            </h2>
          </div>
          <div className="flex gap-2 flex-wrap" data-testid="category-filters">
            {["all", "handmade", "digital", "general"].map((c) => (
              <Link key={c} to={c === "all" ? "/" : `/shop?category=${c}`}
                className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border transition-colors
                  ${category === c ? "bg-signal text-white border-signal" : "border-white/20 hover:border-signal"}`}
                data-testid={`filter-${c}`}>{c}</Link>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="font-mono text-sm opacity-50">Loading...</div>
        ) : products.length === 0 ? (
          <div className="font-mono text-sm opacity-50">No items in this category yet.</div>
        ) : (
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-6"
            initial="hidden" animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07 } } }}>
            {products.map((p, i) => {
              // Bento-ish: first item big, others normal
              const span = i === 0 ? "lg:col-span-8 lg:row-span-2" : "lg:col-span-4";
              const h = i === 0 ? "h-[560px]" : "h-[260px]";
              return (
                <motion.div key={p.id} className={`${span}`}
                  variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}>
                  <Link to={`/product/${p.id}`} className="group block border border-white/10 bg-[#111111] product-tile"
                        data-testid={`product-card-${p.id}`}>
                    <div className={`${h} overflow-hidden bg-neutral-900 relative`}>
                      {p.featured && (
                        <span className="absolute top-3 left-3 z-10 bg-signal text-white px-2 py-1 font-mono text-[10px] tracking-widest uppercase shadow-[0_0_14px_rgba(255,7,58,0.7)]"
                              data-testid={`featured-badge-${p.id}`}>Featured · Ad</span>
                      )}
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name}
                             className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package size={48} weight="duotone" className="text-signal opacity-60" />
                        </div>
                      )}
                    </div>
                    <div className="p-5 flex items-start justify-between gap-4 border-t border-white/10">
                      <div>
                        <p className="overline opacity-50 mb-1">[{(p.category || "general").toUpperCase()}]</p>
                        <h3 className="font-display font-bold text-lg md:text-xl tracking-tight">{p.name}</h3>
                      </div>
                      <p className="font-mono text-lg">${p.price.toFixed(2)}</p>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </section>
    </main>
  );
}
